import { useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";

import { Autocomplete, CircularProgress } from "@mui/material";

import { apiClient } from "@/features/shared";
import { CustomInputField } from "@/features/ui";
import { OutletRoleEnum } from "../outlet-role";

// Cache for outlets to avoid refetching
let outletsCache = null;
let outletsCachePromise = null;

const fetchOutletsFromAPI = async () => {
  // Return cached data if available
  if (outletsCache) {
    return outletsCache;
  }

  // Return existing promise if fetch is in progress
  if (outletsCachePromise) {
    return outletsCachePromise;
  }

  // Create new fetch promise
  outletsCachePromise = apiClient
    .get("outlets")
    .then(({ data: { data } }) => {
      outletsCache = data;
      outletsCachePromise = null;
      return data;
    })
    .catch((error) => {
      console.error("Error fetching outlets:", error);
      outletsCachePromise = null;
      return [];
    });

  return outletsCachePromise;
};

export const AllOutletsSelect = ({
  value,
  onChange,
  disabled,
  TextFieldProps,
  allowedRoles = [OutletRoleEnum.HUB, OutletRoleEnum.RETAIL],
  ...props
}) => {
  const [allOutlets, setAllOutlets] = useState(outletsCache || []);
  const [loading, setLoading] = useState(!outletsCache);

  useEffect(() => {
    const loadOutlets = async () => {
      setLoading(true);
      const data = await fetchOutletsFromAPI();
      setAllOutlets(data);
      setLoading(false);
    };

    loadOutlets();
  }, []); // Only fetch once on mount

  // Filter outlets by allowed roles (memoized)
  const filteredOutlets = useMemo(
    () => allOutlets.filter((outlet) => allowedRoles.includes(outlet.role)),
    [allOutlets, allowedRoles]
  );

  return (
    <Autocomplete
      value={value ?? null}
      onChange={(_, value) => onChange(value)}
      disabled={disabled || loading}
      options={filteredOutlets}
      loading={loading}
      getOptionLabel={(option) => option?.name ?? ""}
      isOptionEqualToValue={(option, selectedOption) =>
        option.id === selectedOption.id
      }
      renderInput={(params) => (
        <CustomInputField
          {...TextFieldProps}
          {...params}
          InputProps={{
            placeholder:
              TextFieldProps?.InputProps?.placeholder || "Pilih Outlet",
            ...params.slotProps.input,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.slotProps.input.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...props}
    />
  );
};

AllOutletsSelect.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  TextFieldProps: PropTypes.object,
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
};
